import { Spinner } from '@/components/ui/spinner';
import { Session } from '@transpordiamet/shared';
import { useAtom, useAtomValue } from 'jotai';
import { lazy, Suspense, useEffect, useState } from 'react';
import { Outlet, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { AdminLayout } from './components/admin/AdminLayout';
import { PermissionsOverlay } from './components/PermissionsOverlay';
import { OfflineBanner } from './components/OfflineBanner';
import { LoadingFallback } from './components/LoadingFallback';
import api from './lib/api';
import { addLangParam } from './lib/utils';
import { localeAtom, sessionAtom } from './lib/atoms';
import { getErrorMessage } from './lib/errors';
import { useSessionTimeout } from './hooks/useSessionTimeout';
import { NotFoundPage } from './routes/404';
import SessionEntryPage from './routes/session-entry';
import StepPage from './routes/step';

// Lazy load admin routes (loaded on demand - ~60KB)
const ConfigurationEditor = lazy(() => import('@/routes/admin/configuration'));
const ConfigurationsList = lazy(() => import('@/routes/admin/configurations'));
const VehicleCategoriesList = lazy(() => import('@/routes/admin/categories'));
const VehicleCategoryEditor = lazy(() => import('@/routes/admin/category'));
const AdminLogin = lazy(() => import('@/routes/admin/index'));
const SessionsList = lazy(() => import('@/routes/admin/sessions'));
const SessionDetail = lazy(() => import('@/routes/admin/session'));
const UsersList = lazy(() => import('@/routes/admin/users'));
const TokensList = lazy(() => import('@/routes/admin/tokens'));

// Lazy load terminal routes (loaded at end of session - ~15-20KB)
const ExpiredPage = lazy(() => import('@/routes/expired'));
const RestartPage = lazy(() => import('@/routes/restart'));
const SuccessPage = lazy(() => import('@/routes/success'));

export function SessionLayout() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const locale = useAtomValue(localeAtom);

  const [session, setSession] = useAtom(sessionAtom);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (sessionId) Sentry.setTag('session_id', sessionId);
  }, [sessionId]);

  // Monitor session timeout and redirect to restart page when expired
  useSessionTimeout();

  // Fetch session when sessionId or locale changes.
  // Locale drives Accept-Language (see ApiLocaleSync); backend rebuilds session.url
  // with the current language so QR/copy stay in sync without client URL hacks.
  useEffect(() => {
    if (!sessionId) return;

    const controller = new AbortController();
    // Only show full-page spinner on first load / session switch — not language switches
    const isInitialLoad = !session || session.id !== sessionId;
    if (isInitialLoad) {
      setLoading(true);
      setError(null);
      setExpired(false);
    }

    api
      .get<Session>(`/sessions/${sessionId}`, { signal: controller.signal })
      .then((response) => {
        setSession(response.data);
        setError(null);
        setExpired(false);
      })
      .catch((err) => {
        if (err.name !== 'AbortError' && err.code !== 'ERR_CANCELED') {
          // A session is unreachable only once expired: the row is deleted on expiry, so 404 means expired.
          if (err.response?.status === 404) {
            setSession(null);
            setExpired(true);
          }
          setError(getErrorMessage(err));
        }
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      controller.abort();
    };
    // session is intentionally omitted — only re-fetch on sessionId/locale change
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid refetch loop on session atom updates
  }, [sessionId, locale, setSession]);

  // useEffect 2: Navigate based on session state (listens to session atom changes from step page)
  useEffect(() => {
    if (!session) return;

    // Redirect to success page if session is completed and we're not already there
    if (session.state === 'completed' && !location.pathname.endsWith('/success')) {
      navigate(addLangParam('success', searchParams), { replace: true });
    }

    // Redirect to expired page if session is expired and we're not already there
    if (session.state === 'expired' && !location.pathname.endsWith('/expired')) {
      navigate(addLangParam('expired', searchParams), { replace: true });
    }
  }, [session, location.pathname, navigate, searchParams]);

  if (loading) {
    return (
      <div className="w-full h-[80vh] flex items-center justify-center">
        <Spinner className="size-6" />
      </div>
    );
  }

  // Before the Outlet, so neither the desktop QR landing nor the mobile steps can render.
  if (expired) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <ExpiredPage />
      </Suspense>
    );
  }

  if (error) {
    return <NotFoundPage />;
  }

  return (
    <>
      <OfflineBanner />
      <Outlet />
      <PermissionsOverlay />
    </>
  );
}

function App() {
  return (
    <Routes>
      {/* Admin Routes - Lazy loaded (~60KB chunk) */}
      <Route
        path="/admin"
        element={
          <Suspense fallback={<LoadingFallback />}>
            <AdminLogin />
          </Suspense>
        }
      />
      <Route path="/admin" element={<AdminLayout />}>
        <Route
          path="sessions"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <SessionsList />
            </Suspense>
          }
        />
        <Route
          path="sessions/:id"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <SessionDetail />
            </Suspense>
          }
        />
        <Route
          path="configurations"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <ConfigurationsList />
            </Suspense>
          }
        />
        <Route
          path="configurations/:id"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <ConfigurationEditor />
            </Suspense>
          }
        />
        <Route
          path="categories"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <VehicleCategoriesList />
            </Suspense>
          }
        />
        <Route
          path="categories/:id"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <VehicleCategoryEditor />
            </Suspense>
          }
        />
        <Route
          path="users"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <UsersList />
            </Suspense>
          }
        />
        <Route
          path="tokens"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <TokensList />
            </Suspense>
          }
        />
      </Route>

      {/* Session Routes */}
      <Route path="/:sessionId" element={<SessionLayout />}>
        {/* Desktop → QR landing; mobile → steps (same URL, UA detection) */}
        <Route index element={<SessionEntryPage />} />
        <Route path="steps/:stepId" element={<StepPage />} />

        {/* Terminal routes - Lazy loaded (~15-20KB chunk) */}
        <Route
          path="restart"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <RestartPage />
            </Suspense>
          }
        />
        <Route
          path="success"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <SuccessPage />
            </Suspense>
          }
        />
        <Route
          path="expired"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <ExpiredPage />
            </Suspense>
          }
        />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default App;
